import type { Library } from '@googlemaps/js-api-loader';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';
import { readCspNonce } from 'react-components/maps/cspNonce.util';

/**
 * Stable reference required by `@react-google-maps/api` / LoadScript — do not inline a new
 * array in {@link getGoogleMapsJsApiLoaderOptions} or the script will reload on every render.
 */
const GOOGLE_MAPS_JS_API_LIBRARIES: Library[] = ['places'];

/**
 * Shared {@link https://react-google-maps-api-docs.netlify.app/docs/use-js-api-loader | useJsApiLoader}
 * options for Care Portal. Uses a stable `id` so the script is not loaded twice when both
 * map views and Places autocomplete mount.
 *
 * `nonce` is forwarded by `useJsApiLoader` into `Loader` from
 * `@googlemaps/js-api-loader`, and by `LoadScript` when a caller spreads
 * these options. That is the nonce on the Maps bootstrap script.
 *
 * @returns Loader options including the Places library for autocomplete.
 */
export function getGoogleMapsJsApiLoaderOptions() {
  const nonce = readCspNonce();

  return {
    id: 'google-map-loader-script',
    googleMapsApiKey: getEnvironment().googleMapsApiKey,
    /** Prefer recent weekly builds so Places (New) widgets (e.g. `PlaceAutocompleteElement`) are available. */
    version: 'weekly',
    libraries: GOOGLE_MAPS_JS_API_LIBRARIES,
    ...(nonce ? { nonce } : {}),
  };
}
